import { createContext, useEffect, useState } from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { toast } from "react-toastify";
import { Profile } from "../types/user.types";
import { Get, handleError, Post } from "../utils/fetch.util";
import { io, Socket } from "socket.io-client";
import Cookies from "universal-cookie";
import { Permissions } from "../routes/routes";
import { FiBell } from "react-icons/fi";
import i18next from "i18next";

type ProviderProps = {
  children: React.ReactNode;
};

type User = {
  isASDCAdmin: boolean | undefined;
  isUser: boolean | undefined;
  user?: Profile;
  userData?: any;
  permissions?: Permissions;
};

type UserContextType = (User | null) & {
  signIn: (token: string) => void;
  initUser: () => void;
  signOut: () => void;
  getValidToken: () => string | null;
  getProfile: () => void;
  socket: any;
  serverMaintenance: boolean;
  setServerMaintenance: (value: boolean) => void;
  checkUnderMaintenance: () => void;
  mutedChannels: number[];
  setMutedChannels: (value: number[]) => void;
  clientApp: "eye" | "fte" | "fm" | "";
  setClientApp: (value: "eye" | "fte" | "fm" | "") => void;
  clientApps: any[];
  setClientApps: (value: any[]) => void;
};

type Token = JwtPayload & {
  role: string;
  email: string;
};

const INITIAL_USER: UserContextType = {
  isASDCAdmin: false,
  isUser: false,
  signIn: () => { },
  initUser: () => { },
  signOut: () => { },
  getValidToken: () => null,
  getProfile: () => { },
  socket: null,
  serverMaintenance:
    localStorage.getItem("serverMaintenance") === "true" ? true : false,
  setServerMaintenance: () => { },
  mutedChannels: [],
  setMutedChannels: () => { },
  checkUnderMaintenance: () => { },
  clientApp: "eye",
  setClientApp: () => { },
  clientApps: [],
  setClientApps: () => { },
};

export const CookieDomain =
  import.meta.env.VITE_ON_SERVER === "true"
    ? `.${import.meta.env.VITE_DOMAIN}`
    : `.${window.location.hostname}`;

// here are the state of the user
// if the user types are all set to undifined, it means that the user is not initialized yet
// if the user types are all set to false, it means that the user is not logged in
// if the user is an ASDC admin, isASDCAdmin is set to true
// if the user is a client user, isUser is set to true
// if the user is unauthorized, the user is set to null
export const UserContext = createContext(INITIAL_USER);

export const UserProvider = ({ children }: ProviderProps) => {
  const [user, setUser] = useState<User>({
    isASDCAdmin: undefined,
    isUser: undefined,
  });
  const [clientApps, setClientApps] = useState<any[]>([]);
  const [clientApp, setClientApp] = useState<"eye" | "fte" | "fm" | "">(
    (localStorage.getItem("clientApp") as "eye" | "fte" | "fm" | "") || "eye"
  );

  const [serverMaintenance, setServerMaintenance] = useState<boolean>(
    localStorage.getItem("serverMaintenance") === "true" ? true : false
  );
  const [mutedChannels, setMutedChannels] = useState<number[]>(
    localStorage.getItem("mutedChannels")
      ? JSON.parse(localStorage.getItem("mutedChannels") as string)
      : []
  );

  const getUserData = async (url: string, token: string) => {
    const response = await Get(url, token);

    if (response === undefined) {
      return undefined;
    }

    if (
      response?.message === "This Token Is Expired" ||
      response?.message === "Unauthorized" ||
      response?.message === "Forbidden resource" ||
      response.message === "This Token Is Not Recognized"
    ) {
      toast(
        <div className="flex justify-center items-center gap-3">
          <span>
            <FiBell className={`icon !w-6 !h-6 !mr-0 cursor-pointer`} />
          </span>
          <span className="text-lg">
            Someone logged in with your account, plaese login again
          </span>
        </div>,
        {
          position: i18next.language === "ar" ? "top-left" : "top-right",
          hideProgressBar: true,
        }
      );
      return null;
    }
    if (response?.statusCode === 401) {
      toast(
        <div className="flex justify-center items-center gap-3">
          <span>
            <FiBell className={`icon !w-6 !h-6 !mr-0 cursor-pointer`} />
          </span>
          <span className="text-lg">Session expired, please login again</span>
        </div>,
        {
          position: i18next.language === "ar" ? "top-left" : "top-right",
          hideProgressBar: true,
        }
      );
      return null;
    }
    if (response?.error || !response?.data) {
      handleError(response);
      return undefined;
    }
    const appUrl = window.location.pathname.startsWith("/admin")
      ? `leango-apps/get-all-leango-apps`
      : `leango-apps/get-all-leango-apps-for-client`;
    if (localStorage.getItem("user") === "admin" && appUrl === `leango-apps/get-all-leango-apps-for-client`) return;
    if (localStorage.getItem("user") !== "admin" && appUrl === `leango-apps/get-all-leango-apps`) return;
    const appRes = await Get(
      appUrl,
      getValidToken()
    );
    if (appRes?.status === "success") {
      setClientApps(appRes?.data);
      const apps = appRes?.data.map((app: any) => app.name) || [];
      if (!apps.includes(clientApp)) {
        setClientApp(apps[0] || "");
        localStorage.setItem("clientApp", apps[0] || "");
      }
    } else {
      if (getValidToken()) handleError(appRes);
    }
    const { data } = response;
    localStorage.setItem("clientLogo", data?.asdc_client?.logo_url);
    return data as Profile;
  };

  const deleteCookiesForDomain = (domain: string) => {
    const cookies = new Cookies(null, { path: "/", domain });
    const allCookies = cookies.getAll();

    Object.keys(allCookies).forEach((cookieName) => {
      cookies.remove(cookieName, { path: "/", domain });
    });
    
    // Explicitly remove our token cookies
    cookies.remove("token", { path: "/", domain });
    cookies.remove("adminToken", { path: "/", domain });
    cookies.remove("clientToken", { path: "/", domain });
  };

  const signOut = async () => {
    // await Post("auth/client-web/logout", {}, getValidToken());

    // const cookies = new Cookies(null, {
    //   path: "/",
    //   domain: CookieDomain,
    // });

    // const allCookies = cookies.getAll();
    // cookies.remove("token");

    // Object.keys(allCookies).forEach((cookieName) => {
    //   cookies.remove(cookieName);
    // });

    // Remove cookies for the top-level domain
    deleteCookiesForDomain("leango.app");

    // Remove cookies for subdomains
    deleteCookiesForDomain("inspection.leango.app");
    deleteCookiesForDomain("inspection-dev.leango.app");
    deleteCookiesForDomain("localhost");

    socket.disconnect();

    localStorage.removeItem("serverMaintenance");
    localStorage.removeItem("clientLogo");
    localStorage.removeItem("mutedChannels");
    setServerMaintenance(false);

    setUser({ isASDCAdmin: false, isUser: false });
    const facIcon = document.querySelector("link[rel*='icon']");
    facIcon?.setAttribute("href", "/gli-GIF 1.webp");
  };

  const signIn = async (token: string) => {
    const cookies = new Cookies(null, {
      path: "/",
      domain: CookieDomain,
    });

    const { role }: Token = jwtDecode(token);
    
    // Store token in appropriate cookie based on role
    if (role === "asdc_admin_user") {
      cookies.set("adminToken", token);
      cookies.set("token", token); // Keep for backward compatibility
    } else {
      cookies.set("clientToken", token);
      cookies.set("token", token); // Keep for backward compatibility
    }

    if (role === "asdc_admin_user") {
      const userData = await getUserData("asdc-admin/my-profile", token);
      setUser({
        isASDCAdmin: true,
        isUser: false,
        user: userData === null ? undefined : userData,
      });
    } else {
      let userData;
      userData = await getUserData(
        "asdc-client-user/get-profile-for-web",
        token
      );

      if (
        Object.values(userData?.UserCRUD_Operations).filter((x) => x === null)
          .length > 0
      ) {
        const res = await Get(
          "testing/give-users-and-admins-added-cruds",
          token
        );
        if (res?.status === "success") {
          userData = await getUserData(
            "asdc-client-user/get-profile-for-web",
            token
          );
        }
      }

      setUser({
        isASDCAdmin: false,
        isUser: true,
        user: userData === null ? undefined : userData,
      });
      // checkUnderMaintenance();
    }
  };

  const getProfile = async () => {
    const token = getValidToken();

    if (token) {
      const { role }: Token = jwtDecode(token);
      if (role === "asdc_admin_user") {
        const userData = await getUserData("asdc-admin/my-profile", token);
        if (userData === null) {
          signOut();
          return;
        }
        setUser({ isASDCAdmin: true, isUser: false, user: userData });
      } else {
        let userData = await getUserData(
          "asdc-client-user/get-profile-for-web",
          token
        );
        if (userData === null) {
          signOut();
          return;
        }
        if (
          Object.values(userData?.UserCRUD_Operations).filter((x) => x === null)
            .length > 0
        ) {
          const res = await Get(
            "testing/give-users-and-admins-added-cruds",
            token
          );
          if (res?.status === "success") {
            userData = await getUserData(
              "asdc-client-user/get-profile-for-web",
              token
            );
            if (userData === null) {
              signOut();
              return;
            }
          }
        }
        setUser({ isASDCAdmin: false, isUser: true, user: userData });
      }
    } else {
      setUser({ isASDCAdmin: false, isUser: false });
    }
  };

  const initUser = async () => {
    // this function will check if the user is logged in and set the user state accordingly
    const token = getValidToken();
    if (token) {
      const { role }: Token = jwtDecode(token);
      if (role === "asdc_admin_user") {
        const userData = await getUserData("asdc-admin/my-profile", token);
        if (userData === null) {
          signOut();
          return;
        }
        setUser({ isASDCAdmin: true, isUser: false, user: userData });
      } else {
        let userData = await getUserData(
          "asdc-client-user/get-profile-for-web",
          token
        );
        if (userData === null) {
          signOut();
          return;
        }
        setUser({ isASDCAdmin: false, isUser: true, user: userData });
        if (
          Object.values(userData?.UserCRUD_Operations).filter((x) => x === null)
            .length > 0
        ) {
          const res = await Get(
            "testing/give-users-and-admins-added-cruds",
            token
          );
          if (res?.status === "success") {
            userData = await getUserData(
              "asdc-client-user/get-profile-for-web",
              token
            );
            if (userData === null) {
              signOut();
              return;
            }
            setUser({ isASDCAdmin: false, isUser: true, user: userData });
          }
        }
      }
    } else {
      setUser({ isASDCAdmin: false, isUser: false });
    }
  };

  const checkUnderMaintenance = async () => {
    const response = await Get(
      "asdc-client/check-server-under-maintenance",
      getValidToken()
    );
    // if (response?.message === "Forbidden resource") {
    //   signOut();
    //   return;
    // }
    if (response?.message === "Unauthorized") {
      localStorage.removeItem("serverMaintenance");
      signOut();
      return;
    }
    if (response?.message === "This Token Is Expired") {
      signOut();
      localStorage.removeItem("serverMaintenance");
      setServerMaintenance(false);
      return;
    }
    if (response.data === true) {
      localStorage.removeItem("serverMaintenance");
      setServerMaintenance(false);
      return;
    } else {
      localStorage.setItem("serverMaintenance", "true");
      setServerMaintenance(true);
    }
  };

  const getValidToken = () => {
    const cookies = new Cookies(null, {
      path: "/",
      domain: CookieDomain,
    });
    
    // Determine which token to use based on current context
    const userType = localStorage.getItem("user");
    const isAdminRoute = window.location.pathname.startsWith("/admin");
    
    let token;
    if (userType === "admin" || isAdminRoute) {
      token = cookies.get("adminToken") || cookies.get("token"); // fallback for backward compatibility
    } else {
      token = cookies.get("clientToken") || cookies.get("token"); // fallback for backward compatibility
    }
    
    if (!token) {
      return null;
    }
    try {
      const decodedToken: Token = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp && decodedToken.exp < currentTime) {
        return null;
      }
      return token;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    initUser();
  }, []);

  const API_LINK = import.meta.env.VITE_API_URL;
  
  const socket = io(API_LINK + "asdc_socket_server", {
    extraHeaders: {
      "asdc-auth-token": `Bearer ${getValidToken()}`,
    },
    query: {
      user_id: user?.user?.asdc_client_user_id,
      qrcode: user?.user?.asdc_client?.qrcode,
    },
    autoConnect: user?.user?.asdc_client_user_id !== undefined,
  });

  useEffect(() => {
    // Set up socket event listeners
    socket.on("connect", () => {
      console.log("✅ Connected:", socket.id);
    }).on("connect_error", (error) => {
      console.log("❌ Connect error:", error.message, error);
    }).on("disconnect", (reason) => {
      console.log("⚠️ Disconnected, reason:", reason);
    }).on("error", (error) => {
      console.log("❌ Error:", error.message, error);
    }).on("reconnect", () => {
      console.log("✅ Reconnected:", socket.id);
    }).on("reconnect_error", (error) => {
      console.log("❌ Reconnect error:", error.message, error);
    }).on("reconnect_failed", () => {
      console.log("❌ Reconnect failed");
    });
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        signIn,
        isASDCAdmin: user?.isASDCAdmin,
        isUser: user?.isUser,
        user: [user?.user],
        userData: [user?.user],
        permissions: user?.user?.UserCRUD_Operations,
        initUser,
        signOut,
        getValidToken,
        getProfile,
        socket,
        serverMaintenance,
        setServerMaintenance,
        checkUnderMaintenance,
        mutedChannels,
        setMutedChannels,
        clientApp,
        setClientApp,
        clientApps,
        setClientApps,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
